import {authManager} from '@/components/Auth/AuthManager.ts';
import {apiEndpoint} from '@/utils/Api.ts';
import type {IdCard} from '@/utils/IdCard.ts';
import {storeIdCard} from '@/utils/IdCard.ts';
import {Box, CircularProgress} from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Dialog from '@mui/material/Dialog';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography/Typography';
import {useSnackbar} from 'notistack';
import type * as React from 'react';
import {useEffect} from 'react';

type Props = {
    open: boolean;
    onClose: () => void;
    onDownloadSuccess: (idCard: IdCard) => void;
};

const DownloadIdDialog: React.FC<Props> = ({open, onClose, onDownloadSuccess}: Props) => {
    const {enqueueSnackbar} = useSnackbar();

    useEffect(() => {
        if (!open) {
            return;
        }

        const accessToken = authManager.getAccessToken();

        if (!accessToken) {
            enqueueSnackbar('Your authentication expired, please try again.', {variant: 'error'});
            onClose();
            return;
        }

        void (async () => {
            let response: Response;

            try {
                response = await fetch(new URL('/id-card', apiEndpoint).toString(), {
                    headers: {Authorization: `Bearer ${accessToken}`},
                });
            } catch (e) {
                enqueueSnackbar('Server not reachable, please try again later.', {
                    variant: 'error',
                });
                onClose();
                return;
            }

            if (!response.ok) {
                if (response.status === 401) {
                    enqueueSnackbar('Your authentication expired, please try again.', {
                        variant: 'error',
                    });
                    onClose();
                    return;
                }

                if (response.status === 409) {
                    const data = (await response.json()) as {hint: string};

                    if (data.hint === 'no-photo-submitted') {
                        enqueueSnackbar("You haven't submitted a photo yet.", {variant: 'error'});
                        onClose();
                        return;
                    }

                    if (data.hint === 'photo-pending-validation') {
                        enqueueSnackbar('Your photo is still pending validation.', {
                            variant: 'error',
                        });
                        onClose();
                        return;
                    }
                }

                enqueueSnackbar('An unknown error occurred.', {variant: 'error'});
                onClose();
                return;
            }

            const rawIdCard = await response.text();
            let idCard: IdCard;

            try {
                idCard = await storeIdCard(rawIdCard);
            } catch (e) {
                enqueueSnackbar('An unknown error occurred.', {variant: 'error'});
                onClose();
                return;
            }

            enqueueSnackbar('ID card has been downloaded.', {variant: 'success'});
            onDownloadSuccess(idCard);
            onClose();
        })();
    }, [open, onClose, onDownloadSuccess, enqueueSnackbar]);

    return (
        <Dialog open={open} fullScreen>
            <AppBar position='static'>
                <Toolbar>
                    <Typography variant='h6'>Downloading ID…</Typography>
                </Toolbar>
            </AppBar>

            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    height: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <CircularProgress />
            </Box>
        </Dialog>
    );
};

export default DownloadIdDialog;
